import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { Button } from "@/components/ui/button";

export interface SolutionContent {
  slug: string;
  title: string;
  description: string;
  eyebrow: string;
  h1: string;
  intro: string;
  benefits: { h: string; p: string }[];
  steps: string[];
  faqs: { q: string; a: string }[];
}

const SITE = "https://echocrm.app";

export const SolutionPage = ({ c }: { c: SolutionContent }) => {
  const url = `${SITE}/${c.slug}`;
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const appLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "EchoCRM",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url,
    offers: { "@type": "Offer", price: "59", priceCurrency: "USD" },
  };

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
        <meta name="description" content={c.description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={c.title} />
        <meta property="og:description" content={c.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(appLd)}</script>
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <MarketingNav />

        <section className="theme-glow">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-14 sm:pb-20">
            <div className="max-w-3xl">
              <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-4">{c.eyebrow}</p>
              <h1 className="font-display font-semibold text-[36px] sm:text-[56px] tracking-[-0.035em] leading-[1.05]">{c.h1}</h1>
              <p className="mt-6 text-[16px] sm:text-[18px] text-muted-foreground max-w-2xl">{c.intro}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg">
                  <Link to="/signup">Start 7-day free trial <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/pricing">See pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {c.benefits.map((b) => (
              <article key={b.h} className="rounded-xl border border-border bg-card p-6">
                <h2 className="font-display font-semibold text-[18px] tracking-tight">{b.h}</h2>
                <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">{b.p}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="border-y border-border bg-muted/30">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
            <h2 className="font-display font-semibold text-[28px] sm:text-[36px] tracking-[-0.03em]">Up and running in minutes</h2>
            <ol className="mt-8 grid gap-4 sm:grid-cols-3">
              {c.steps.map((s, i) => (
                <li key={s} className="rounded-xl border border-border bg-background p-6">
                  <span className="font-mono-accent text-[12px] text-primary">0{i + 1}</span>
                  <p className="mt-2 text-[15px]">{s}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-5 sm:px-8 py-16 sm:py-20">
          <h2 className="font-display font-semibold text-[28px] sm:text-[36px] tracking-[-0.03em]">Frequently asked questions</h2>
          <div className="mt-8 divide-y divide-border border-y border-border">
            {c.faqs.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none font-medium text-[16px] flex justify-between gap-4">
                  {f.q}<span className="text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[15px] text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pb-20">
          <div className="rounded-2xl border border-border bg-card p-8 sm:p-12 text-center">
            <h2 className="font-display font-semibold text-[28px] sm:text-[36px] tracking-[-0.03em]">Try EchoCRM free for 7 days</h2>
            <ul className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[14px] text-muted-foreground">
              {["Import your contacts from CSV", "Cancel anytime", "Pro from $59/month"].map((t) => (
                <li key={t} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />{t}</li>
              ))}
            </ul>
            <Button asChild size="lg" className="mt-8">
              <Link to="/signup">Start free trial <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>

        <MarketingFooter />
      </main>
    </>
  );
};
